import {
  CandidateOpportunitiesConst,
  CandidateStatusLabelsMap,
  PositionsConst,
} from '@axiom/const';
import { DateUtil } from '@axiom/ui';
import type { OpportunityCandidate, Position } from '@axiom/validation';
import { CurrencyUtil } from '@axiom/utils';

const { EndDateStatuses } = PositionsConst;
const { CandidateStatuses, CandidateStatusLabels: CandidateStatusLabelsConst } =
  CandidateOpportunitiesConst;
const CandidateStatusLabels: Record<string, string> =
  CandidateStatusLabelsConst;

const DASH = '-';

export const TalentOpportunityCardTypeNames = {
  inConsideration: 'In Consideration',
  currentEngagements: 'Current Engagements',
  previousEngagements: 'Previous Engagements',
  noLongerInConsideration: 'No Longer in Consideration',
} as const;

const TalentOpportunityCardTypes = Object.values(
  TalentOpportunityCardTypeNames
);

export type TalentOpportunityCardType =
  (typeof TalentOpportunityCardTypes)[number];

type OtherCandidatesStatusOnPosition = NonNullable<
  NonNullable<
    OpportunityCandidate['position']
  >['otherCandidatesStatusOnPosition']
>;

export const TalentOpportunityCardUtil = {
  DASH,
  EndDateStatuses,
  CandidateStatuses,
  CandidateStatusLabels,

  talentOpportunityCardTypeNameFormatter: (type: TalentOpportunityCardType) => {
    return type.replaceAll(' ', '-').toLowerCase();
  },

  formatCreateOrUpdateDate: (date: string | null, isCreate: boolean) => {
    if (date) {
      const displayDate = DateUtil.displayTimestamp(date);
      const daysOld = DateUtil.getCountFromDateToDate(
        new Date().toISOString(),
        date
      );

      if (daysOld === 0) {
        return displayDate;
      } else if (daysOld === 1) {
        return `${displayDate} (1 day ${isCreate ? 'old' : 'ago'})`;
      }
      return `${displayDate} (${daysOld} days ${isCreate ? 'old' : 'ago'})`;
    }
    return DASH;
  },

  formatWeeklyTimeUnit: (weeklyTimeUnit: Position['weeklyTimeUnit']) => {
    return weeklyTimeUnit === 'Daily' ? 'Days/Week' : 'Hours/Week';
  },

  formatBilling: (position?: Position | null) => {
    if (position?.billingUnitsPerWeek) {
      return `${position.billingUnitsPerWeek} ${TalentOpportunityCardUtil.formatWeeklyTimeUnit(
        position.weeklyTimeUnit
      )} ${position.reservedType ? `(${position.reservedType})` : ''}`;
    }
    return DASH;
  },

  formatChangedStatus: (opportunityCandidate: OpportunityCandidate) => {
    const date = DateUtil.displayTimestamp(
      opportunityCandidate.candidateStatusUpdatedAt
    );

    if (date) {
      if (opportunityCandidate.previousCandidateStatus) {
        return `${date} (previously ${
          CandidateStatusLabelsMap.get(
            opportunityCandidate.previousCandidateStatus
          ) || 'N/A'
        })`;
      }
      return `${date} (previously N/A)`;
    }
    return DASH;
  },

  formatCompensation: (opportunityCandidate: OpportunityCandidate) => {
    if (opportunityCandidate.proposedHourlyCompensation) {
      let rate = '';
      if (opportunityCandidate.compensationType) {
        rate += `${opportunityCandidate.compensationType}, `;
      }

      rate += CurrencyUtil.getFormattedCurrency({
        value: opportunityCandidate.proposedHourlyCompensation,
        currencyCode: opportunityCandidate.currency,
      });

      rate += '/Hourly';

      if (opportunityCandidate.proposedMarginTarget) {
        rate += ` (${opportunityCandidate.proposedMarginTarget}%)`;
      }
      return rate;
    }
    return DASH;
  },

  isConsiderationCard: (type: TalentOpportunityCardType) => {
    return (
      type === TalentOpportunityCardTypeNames.inConsideration ||
      type === TalentOpportunityCardTypeNames.noLongerInConsideration
    );
  },

  isEngagementCard: (type: TalentOpportunityCardType) => {
    return (
      type === TalentOpportunityCardTypeNames.currentEngagements ||
      type === TalentOpportunityCardTypeNames.previousEngagements
    );
  },

  getCandidatesWithStatus: (
    otherCandidates: OtherCandidatesStatusOnPosition | null | undefined,
    status: string,
    includeNames = false
  ): string | undefined => {
    const candidateList = (otherCandidates || []).filter(
      c => c.status === status
    );

    if (candidateList.length > 0) {
      let returnValue = `${candidateList.length} ${CandidateStatusLabels[status]}`;
      if (includeNames) {
        returnValue += ` (${candidateList
          .map(c => c.candidateName)
          .join(', ')})`;
      }

      return returnValue;
    }
    return undefined;
  },

  buildTalentOnPosition: (opportunityCandidate: OpportunityCandidate) => {
    const otherCandidates =
      opportunityCandidate.position?.otherCandidatesStatusOnPosition;

    return [
      TalentOpportunityCardUtil.getCandidatesWithStatus(
        otherCandidates,
        CandidateStatuses.Completed,
        true
      ),
      TalentOpportunityCardUtil.getCandidatesWithStatus(
        otherCandidates,
        CandidateStatuses.Engaged,
        true
      ),
      TalentOpportunityCardUtil.getCandidatesWithStatus(
        otherCandidates,
        CandidateStatuses.Selected,
        true
      ),
      TalentOpportunityCardUtil.getCandidatesWithStatus(
        otherCandidates,
        CandidateStatuses.Interviewing
      ),
      TalentOpportunityCardUtil.getCandidatesWithStatus(
        otherCandidates,
        CandidateStatuses.Submitted
      ),
      TalentOpportunityCardUtil.getCandidatesWithStatus(
        otherCandidates,
        CandidateStatuses.WarmedYes
      ),
      TalentOpportunityCardUtil.getCandidatesWithStatus(
        otherCandidates,
        CandidateStatuses.Warmed
      ),
    ]
      .filter(Boolean)
      .join(', ');
  },
};
